import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, ArrowLeft, Bank, ShoppingCart, UsersFour, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function Taxas() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/fees");
        setRates(data);
      } catch {
        // Fallback caso o endpoint público falhe
        setRates({ bank_fee_rate: 0.03, sales_commission_rate: 0.08, default_affiliate_rate: 0.10 });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pct = (v) => `${(Number(v || 0) * 100).toFixed(v < 0.01 ? 2 : 0).replace(/\.0$/, "")}%`;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="taxas-page">
      {/* NAV */}
      <nav className="glass-nav sticky top-0 z-50 px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="taxas-back-home">
          <Lightning size={26} weight="fill" color="var(--accent)" />
          <span className="font-display font-black text-xl tracking-tight">MERCADOPAGU</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-ghost text-sm">Entrar</Link>
          <Link to="/register" className="btn-primary text-sm">Abrir conta</Link>
        </div>
      </nav>

      <section className="hero-grid-bg px-8 md:px-16 pt-20 pb-16">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6" data-testid="taxas-breadcrumb">
            <ArrowLeft size={16} weight="bold" /> Voltar para o início
          </Link>
          <div className="label-caps mb-4 inline-flex items-center gap-2 px-3 py-1 border border-[var(--border)] rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] animate-pulse" />
            Transparência total · Sem letras miúdas
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl leading-[0.95] tracking-tighter mb-6">
            Nossas <span className="text-[var(--accent)]">taxas</span>.<br />Simples e diretas.
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            Sem mensalidade. Sem taxa de setup. Você só paga quando movimenta ou vende. Todas as tarifas
            abaixo são cobradas automaticamente pela plataforma no momento da operação.
          </p>
        </div>
      </section>

      <section className="px-8 md:px-16 py-16">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-4">
          {/* Taxa Bancária */}
          <div className="card-flat p-8 border-l-2 border-[var(--accent)]" data-testid="fee-card-bank">
            <Bank size={32} color="var(--accent)" weight="duotone" />
            <div className="label-caps mt-6 mb-1">Operações bancárias</div>
            <div className="font-display font-black text-5xl text-[var(--accent)] tracking-tighter my-3">
              {loading ? "…" : pct(rates?.bank_fee_rate)}
            </div>
            <div className="text-sm text-white/60 leading-relaxed mb-4">
              Cobrado por operação de Pix, transferência entre contas e saque. Descontado automaticamente do valor movimentado.
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              {["Depósito Pix", "Transferência interna", "Saque via Pix"].map((it) => (
                <li key={it} className="flex items-center gap-2">
                  <CheckCircle size={14} color="var(--accent)" weight="fill" /> {it}
                </li>
              ))}
            </ul>
          </div>

          {/* Taxa Venda */}
          <div className="card-flat p-8 border-l-2 border-[var(--accent)]" data-testid="fee-card-sales">
            <ShoppingCart size={32} color="var(--accent)" weight="duotone" />
            <div className="label-caps mt-6 mb-1">Venda de infoproduto</div>
            <div className="font-display font-black text-5xl text-[var(--accent)] tracking-tighter my-3">
              {loading ? "…" : pct(rates?.sales_commission_rate)}
            </div>
            <div className="text-sm text-white/60 leading-relaxed mb-4">
              Retido pela plataforma em cada venda de funil aprovada. O restante cai automaticamente na sua conta MP via split.
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              {["Split automático (MP Marketplace)", "Sem taxa por link inativo", "Sem cobrança de armazenamento"].map((it) => (
                <li key={it} className="flex items-center gap-2">
                  <CheckCircle size={14} color="var(--accent)" weight="fill" /> {it}
                </li>
              ))}
            </ul>
          </div>

          {/* Afiliados */}
          <div className="card-flat p-8 border-l-2 border-white/20" data-testid="fee-card-affiliate">
            <UsersFour size={32} color="var(--fg)" weight="duotone" />
            <div className="label-caps mt-6 mb-1">Comissão de afiliado</div>
            <div className="font-display font-black text-5xl tracking-tighter my-3">
              até <span className="text-[var(--accent)]">50%</span>
            </div>
            <div className="text-sm text-white/60 leading-relaxed mb-4">
              Padrão de <b className="text-white">{loading ? "…" : pct(rates?.default_affiliate_rate)}</b>, mas <b className="text-white">quem define é o produtor</b> — pode configurar 0% a 50% por funil.
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              {["Configurável por funil", "Pago do bolso do produtor", "Plataforma não retém nada extra"].map((it) => (
                <li key={it} className="flex items-center gap-2">
                  <CheckCircle size={14} color="var(--accent)" weight="fill" /> {it}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Exemplo prático */}
        <div className="max-w-4xl mx-auto mt-16">
          <div className="label-caps mb-4">Exemplo prático</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-8">
            Uma venda de <span className="text-[var(--accent)]">R$ 100,00</span> por afiliado, na prática.
          </h2>
          <div className="card-flat p-8">
            <div className="grid sm:grid-cols-4 gap-4 text-center" data-testid="fee-example">
              <div>
                <div className="label-caps mb-2">Cliente paga</div>
                <div className="font-display font-black text-3xl">R$ 100,00</div>
              </div>
              <div>
                <div className="label-caps mb-2">Plataforma</div>
                <div className="font-display font-black text-3xl text-[var(--accent)]">
                  R$ {loading ? "—" : (100 * (rates?.sales_commission_rate || 0)).toFixed(2).replace(".", ",")}
                </div>
                <div className="text-xs text-white/50 mt-1">{loading ? "" : pct(rates?.sales_commission_rate)} retido</div>
              </div>
              <div>
                <div className="label-caps mb-2">Afiliado</div>
                <div className="font-display font-black text-3xl">
                  R$ {loading ? "—" : (100 * (rates?.default_affiliate_rate || 0)).toFixed(2).replace(".", ",")}
                </div>
                <div className="text-xs text-white/50 mt-1">{loading ? "" : pct(rates?.default_affiliate_rate)} padrão</div>
              </div>
              <div>
                <div className="label-caps mb-2">Produtor recebe</div>
                <div className="font-display font-black text-3xl text-[var(--success)]">
                  R$ {loading ? "—" : (100 * (1 - (rates?.sales_commission_rate || 0) - (rates?.default_affiliate_rate || 0))).toFixed(2).replace(".", ",")}
                </div>
                <div className="text-xs text-white/50 mt-1">via split MP</div>
              </div>
            </div>
            <div className="border-t border-[var(--border)] mt-6 pt-6 text-xs text-white/50 text-center">
              * Se o produtor não usar afiliados, ele recebe {loading ? "—" : `${(100 - (rates?.sales_commission_rate || 0) * 100).toFixed(0)}%`} do valor. Se configurar afiliado a 50%, o produtor fica com o restante após a taxa da plataforma.
            </div>
          </div>
        </div>

        {/* FAQ Curto */}
        <div className="max-w-4xl mx-auto mt-16 grid md:grid-cols-2 gap-4">
          {[
            { q: "Tem taxa de mensalidade?", a: "Não. Você só paga quando movimenta ou vende. Zero custo fixo." },
            { q: "Tem taxa de saque?", a: `Sim, ${loading ? "" : pct(rates?.bank_fee_rate)} sobre o valor sacado, cobrado no momento do Pix.` },
            { q: "Como funciona o split das vendas?", a: "Usamos o Marketplace do Mercado Pago. O dinheiro cai direto na conta MP do produtor, já descontada a taxa da plataforma." },
            { q: "Quem define a comissão do afiliado?", a: "O próprio produtor, por funil. Vai de 0% a 50% do valor da venda." },
          ].map((f, i) => (
            <div key={i} className="card-flat p-6" data-testid={`faq-${i}`}>
              <div className="font-display font-bold text-lg mb-2">{f.q}</div>
              <div className="text-sm text-white/60 leading-relaxed">{f.a}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="max-w-4xl mx-auto mt-20 text-center">
          <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-4">
            Pronto para começar sem letras miúdas?
          </h3>
          <p className="text-white/60 mb-8">Sem contrato, sem setup, sem burocracia. Abra sua conta em 30 segundos.</p>
          <Link to="/register" data-testid="taxas-cta-register" className="btn-primary inline-flex items-center gap-2">
            Abrir conta grátis
          </Link>
        </div>
      </section>

      <footer className="px-8 md:px-16 py-8 border-t border-[var(--border)] text-white/40 text-sm flex justify-between">
        <div>© 2026 MercadoPagu · Infra Mercado Pago</div>
        <div className="flex gap-6">
          <Link to="/" className="hover:text-white">Início</Link>
          <Link to="/login" className="hover:text-white">Entrar</Link>
        </div>
      </footer>
    </div>
  );
}
